/**
 * PWA Offline — ConflictModal Component
 *
 * Shows when a sync conflict is detected (HTTP 409).
 * Displays the user's local version vs the server version and lets the user choose.
 *
 * Design decisions (from design.md Section 5 — Conflict Resolution):
 * - Default: SERVER WINS — overwrite local with server data
 * - User option: "Keep my version" — force update via PATCH with `?force=true`
 * - ConflictModal: shows user what changed and lets them choose
 *
 * Task 4.3: Implement ConflictModal component
 */

import { Modal, Button, Divider, Typography, Space } from 'antd';
import { ExclamationCircleOutlined, CloudOutlined, DatabaseOutlined } from '@ant-design/icons';
import { useState } from 'react';

const { Text, Title } = Typography;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ConflictData {
    /** The entity type (e.g., 'consultation', 'appointment') */
    entityType: string;
    /** The entity ID */
    entityId: number;
    /** The user's local version of the data */
    localData: Record<string, unknown>;
    /** The server's version of the data */
    serverData: Record<string, unknown>;
}

export interface ConflictModalProps {
    /** Whether the modal is open */
    open: boolean;
    /** The conflict data to display */
    conflict: ConflictData | null;
    /** Callback when user chooses to keep the server version */
    onKeepServer: (entityType: string, entityId: number) => void;
    /** Callback when user chooses to force-update with their local version */
    onKeepLocal: (entityType: string, entityId: number) => void;
    /** Callback when modal is closed (same as keep server) */
    onClose: () => void;
}

// ── Helper: Format field name for display ─────────────────────────────────────

function formatFieldName(field: string): string {
    return field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
}

// ── Helper: Format value for display ──────────────────────────────────────────

function formatValue(value: unknown): string {
    if (value === null || value === undefined) {
        return '(vacío)';
    }
    if (typeof value === 'boolean') {
        return value ? 'Sí' : 'No';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

// ── Helper: Find changed fields between local and server ──────────────────────

function getChangedFields(
    localData: Record<string, unknown>,
    serverData: Record<string, unknown>
): string[] {
    const allKeys = new Set([...Object.keys(localData), ...Object.keys(serverData)]);
    const changed: string[] = [];

    for (const key of allKeys) {
        if (JSON.stringify(localData[key]) !== JSON.stringify(serverData[key])) {
            changed.push(key);
        }
    }

    return changed;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * ConflictModal displays a comparison between local and server data
 * when a sync conflict is detected (HTTP 409).
 *
 * It provides two actions:
 * 1. "Keep Server" — discards the local changes and accepts server data
 * 2. "Keep My Version" — forces the local changes to overwrite server data
 */
export function ConflictModal({
    open,
    conflict,
    onKeepServer,
    onKeepLocal,
    onClose,
}: ConflictModalProps) {
    const [selectedVersion, setSelectedVersion] = useState<'server' | 'local'>('server');

    if (!conflict) {
        return null;
    }

    const changedFields = getChangedFields(conflict.localData, conflict.serverData);

    const handleKeepServer = () => {
        onKeepServer(conflict.entityType, conflict.entityId);
        onClose();
    };

    const handleKeepLocal = () => {
        onKeepLocal(conflict.entityType, conflict.entityId);
        onClose();
    };

    return (
        <Modal
            title={
                <Space>
                    <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                    <span>Conflicto de Sincronización Detectado</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={600}
            destroyOnClose
        >
            <div style={{ padding: '16px 0' }}>
                {/* Header explanation */}
                <Text type="secondary">
                    Los datos en el servidor han cambiado desde que comenzó a editar sin conexión.
                    Por favor, elija qué versión conservar.
                </Text>

                <Divider />

                {/* Version selector */}
                <Space style={{ marginBottom: 16 }}>
                    <Button
                        type={selectedVersion === 'server' ? 'primary' : 'default'}
                        icon={<CloudOutlined />}
                        onClick={() => setSelectedVersion('server')}
                    >
                        Versión del Servidor
                    </Button>
                    <Button
                        type={selectedVersion === 'local' ? 'primary' : 'default'}
                        icon={<DatabaseOutlined />}
                        onClick={() => setSelectedVersion('local')}
                    >
                        Mi Versión Local
                    </Button>
                </Space>

                {/* Comparison table */}
                <div
                    style={{
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        overflow: 'hidden',
                    }}
                >
                    {/* Table header */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            background: '#fafafa',
                            padding: '12px 16px',
                            fontWeight: 600,
                            fontSize: 13,
                        }}
                    >
                        <div>Campo</div>
                        <div
                            style={{
                                color: selectedVersion === 'server' ? '#1890ff' : undefined,
                                fontWeight: selectedVersion === 'server' ? 700 : 400,
                            }}
                        >
                            Versión del Servidor {selectedVersion === 'server' && '✓'}
                        </div>
                        <div
                            style={{
                                color: selectedVersion === 'local' ? '#1890ff' : undefined,
                                fontWeight: selectedVersion === 'local' ? 700 : 400,
                            }}
                        >
                            Su Versión Local {selectedVersion === 'local' && '✓'}
                        </div>
                    </div>

                    {/* Changed fields */}
                    {changedFields.map((field) => (
                        <div
                            key={field}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr',
                                padding: '12px 16px',
                                borderTop: '1px solid #f0f0f0',
                                background:
                                    selectedVersion === 'server' && conflict.serverData[field] !== conflict.localData[field]
                                        ? '#e6f7ff'
                                        : selectedVersion === 'local' && conflict.serverData[field] !== conflict.localData[field]
                                        ? '#fff7e6'
                                        : undefined,
                            }}
                        >
                            <div style={{ fontWeight: 500 }}>
                                <Text code style={{ fontSize: 12 }}>
                                    {formatFieldName(field)}
                                </Text>
                            </div>
                            <div>
                                <Text
                                    delete={selectedVersion === 'local'}
                                    style={{
                                        color:
                                            selectedVersion === 'server' &&
                                            conflict.serverData[field] !== conflict.localData[field]
                                                ? '#1890ff'
                                                : undefined,
                                    }}
                                >
                                    {formatValue(conflict.serverData[field])}
                                </Text>
                            </div>
                            <div>
                                <Text
                                    delete={selectedVersion === 'server'}
                                    style={{
                                        color:
                                            selectedVersion === 'local' &&
                                            conflict.serverData[field] !== conflict.localData[field]
                                                ? '#1890ff'
                                                : undefined,
                                    }}
                                >
                                    {formatValue(conflict.localData[field])}
                                </Text>
                            </div>
                        </div>
                    ))}

                    {/* Unchanged fields (collapsed) */}
                    {changedFields.length === 0 && (
                        <div
                            style={{
                                padding: '24px 16px',
                                textAlign: 'center',
                                color: '#999',
                            }}
                        >
                            No hay diferencias visibles en los campos modificados
                        </div>
                    )}
                </div>

                <Divider />

                {/* Actions */}
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={handleKeepServer}>
                        Conservar Versión del Servidor
                    </Button>
                    <Button type="primary" danger onClick={handleKeepLocal}>
                        Conservar Mi Versión
                    </Button>
                </Space>

                {/* Warning for clinical data */}
                <div style={{ marginTop: 16 }}>
                    <Text type="warning" style={{ fontSize: 12 }}>
                        Nota: Los datos clínicos deben revisarse cuidadosamente. En caso de duda,
                        consulte con un supervisor antes de sobrescribir los datos del servidor.
                    </Text>
                </div>
            </div>
        </Modal>
    );
}

export default ConflictModal;
